import { Component, computed } from '@angular/core';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { wasmVectorToArray } from '../../utils/wasmVector';
import { JSBridgeService } from '../../core/jsbridge.service';
import { ModulesRepoService } from '../modules-repo.service';
import { MetaService } from '../../core/meta.service';
import { eAfdelingType } from '@wasm/KopWeb';
import { AfdModuleMeta } from '@etron/typings';
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import { FilterService } from '@progress/kendo-angular-grid';

export type MetaPropInfo = {
  index: number,
  afdType: string,
  type: "ai" | "losdef" | "droogwand" | "koeling" | "verwarming" | "servo",
  name: string,
  modules: {name: string, key: string}[]
}

export type MetaPropInfoStr = {
  index: number,
  afdType: string,
  type: "ai" | "losdef" | "droogwand" | "koeling" | "verwarming" | "servo",
  name: string,
  modules: {name: string, key: string}[],
  modulesStr: string,  
}


@Component({
  selector: 'modules-meta-overview',
  templateUrl: './meta-overview.component.html',
  styles: ``
})
export class MetaOverviewComponent  {

  constructor(public jsBridgeService: JSBridgeService, 
    public modulesRepo: ModulesRepoService, public metaService: MetaService) {

  }

  public allAfd = computed(() => {
    let tmp = this.jsBridgeService.getBridgeSync().getAfdelingsInfo();
    let ret = wasmVectorToArray(tmp)
      .filter(a => a.valid4version && a.is_afdelin)
      .sort((a, b) => a.type - b.type);
    tmp.delete();
    return ret;
  });

  public allAfdTypes = computed(() => {
    let allaAfd = this.allAfd();
    let ret =  allaAfd.map(a => a.naam.toString());
    console.log(ret);
    return ret;
  });

  public allTypes: string[] = ["ai", "losdef", "droogwand", "koeling", "verwarming", "servo"];


  private buildModulesInfo(afdType: number) {
    let modules = this.modulesRepo.getModules(afdType);
    let ret: Record<string, AfdModuleMeta[]> = {};
    for (let m of modules) {
      // Ais
      if (m.allAnalogIngagen != undefined) {
        for (let mai of m.allAnalogIngagen) {
          let key = `ai|${mai}|${afdType}`;
          if (ret[key] == undefined) {
            ret[key] = [m];
          } else {
            ret[key].push(m);
          }
        }          
      }

      // Los defs
      if (m.allLosDefsIndexes != undefined) {
        for (let ldi of m.allLosDefsIndexes) {
          let key = `losdef|${ldi}|${afdType}`;
          if (ret[key] == undefined) {
            ret[key] = [m];
          } else {
            ret[key].push(m);
          }
        }
      }

      // Servos
      if (m.allServos != undefined) {
        for (let si of m.allServos) {
          let key = `servo|${si}|${afdType}`;
          if (ret[key] == undefined) {
            ret[key] = [m];
          } else {
            ret[key].push(m);
          }
        }
      }

      //Extra Programma
      if (m.allBijzPrgm != undefined) {
        for (let bi of m.allBijzPrgm) {
          let key = `bijz|${bi}|${afdType}`;
          if (ret[key] == undefined) {
            ret[key] = [m];
          } else {
            ret[key].push(m);
          }
        }
      }

      if (m.alarms != undefined) {
        for (let am of m.alarms) {
          let key = `alarm|${am}|${afdType}`;
          if (ret[key] == undefined) {
            ret[key] = [m];
          } else {
            ret[key].push(m);
          }
        }
      }
    }

    return ret;
  }

  private findModulesInfo() {
    let afdTypes = this.allAfd();
    let ret: Record<string, MetaPropInfo> = {};
    for (let afdType of afdTypes) {
      let afdMeta = this.metaService.afdelingMeta(afdType.type);
      let modulesInfo = this.buildModulesInfo(afdType.type);
      if (afdMeta != undefined) {
        for (let ai of afdMeta.ais) {
          let key = `ai|${ai.index}|${afdType.type}`;
          let moduleInfo = modulesInfo[key];
          if (ret[key] == undefined) {
            ret[key] = {
              afdType: afdType.naam.toString(),
              index: ai.index,
              type: "ai",
              name: ai.omschrij.toString(),
              modules: modulesInfo[key]?.map(m => ({
                name: m.name,
                key: m.key
              })) ?? []
            }
          } else {
            ret[key].modules = ret[key].modules.concat(moduleInfo?.map(m => ({
              name: m.name,
              key: m.key
            })))            
          }

        }

        // Los defs
        for (let ld of afdMeta.losDefs) {
          let key = `losdef|${ld.index}|${afdType.type}`;
          let moduleInfo = modulesInfo[key];
          if (ret[key] == undefined) {
            ret[key] = {
              afdType: afdType.naam.toString(),
              index: ld.index,
              type: "losdef",
              name: ld.omschr.toString(),
              modules: modulesInfo[key]?.map(m => ({
                name: m.name,
                key: m.key
              })) ?? []
            }
          } else {
            ret[key].modules = ret[key].modules.concat(moduleInfo?.map(m => ({
              name: m.name,
              key: m.key
            })))            
          }
        }

        // Droog Wand
        for (let dw of afdMeta.drogWands) {
          let key = `losdef|${dw.index}|${afdType.type}`;
          let moduleInfo = modulesInfo[key];
          if (ret[key] == undefined) {
            ret[key] = {
              afdType: afdType.naam.toString(),
              index: dw.index,
              type: "droogwand",
              name: dw.omschr.toString(),
              modules: modulesInfo[key]?.map(m => ({
                name: m.name,
                key: m.key
              })) ?? []
            }
          } else {
            ret[key].modules = ret[key].modules.concat(moduleInfo?.map(m => ({
              name: m.name,
              key: m.key
            })))            
          }
        }

        // verwarming
        for (let vw of afdMeta.verwarmings) {
          let key = `losdef|${vw.index}|${afdType.type}`;
          let moduleInfo = modulesInfo[key];
          if (ret[key] == undefined) {
            ret[key] = {
              afdType: afdType.naam.toString(),
              index: vw.index,
              type: "verwarming",
              name: vw.omschr.toString(),
              modules: modulesInfo[key]?.map(m => ({
                name: m.name,
                key: m.key
              })) ?? []
            }
          } else {
            ret[key].modules = ret[key].modules.concat(moduleInfo?.map(m => ({
              name: m.name,
              key: m.key
            })))            
          }
        }

        // koeling
        for (let ko of afdMeta.koelings) {
          let key = `losdef|${ko.index}|${afdType.type}`;
          let moduleInfo = modulesInfo[key];
          if (ret[key] == undefined) {
            ret[key] = {
              afdType: afdType.naam.toString(),
              index: ko.index,
              type: "koeling",
              name: ko.omschr.toString(),
              modules: modulesInfo[key]?.map(m => ({
                name: m.name,
                key: m.key
              })) ?? []
            }
          } else {
            ret[key].modules = ret[key].modules.concat(moduleInfo?.map(m => ({
              name: m.name,
              key: m.key
            })))            
          }
        }

        // Servos
        for (let s of afdMeta.servos) {
          let key = `servo|${s.index}|${afdType.type}`;
          let moduleInfo = modulesInfo[key];
          if (ret[key] == undefined) {
            ret[key] = {
              afdType: afdType.naam.toString(),
              index: s.index,
              type: "servo",
              name: s.omschr.toString(),
              modules: modulesInfo[key]?.map(m => ({
                name: m.name,
                key: m.key
              })) ?? []
            }
          } else {
            ret[key].modules = ret[key].modules.concat(moduleInfo?.map(m => ({
              name: m.name,
              key: m.key
            })))            
          }
        }
      }
    }

    let ret2: MetaPropInfoStr[] = [];
    for (let [key, value] of Object.entries(ret)) {
        ret2.push({
          ...value,
          modulesStr: value.modules.map(m => `${m.name},${m.key}`).join("| ")
        });
    }

    return ret2;
  }

  public isEditing = false;

  public open() {
    this.isEditing = true;
  }

  public modulesInfo = computed(() => {
    let ret = this.findModulesInfo();
    return ret;
  })

}
